/** Default when neither env nor Vite config is available (local Docker + bot bundle smoke tests). */
const LOCAL_DEFAULT = 'http://localhost:3015';

/** Hosted Medplum project name → public app origin when APP_BASE_URL is unset. */
const HOSTED_APP_URL_BY_PROJECT_NAME: Array<{ pattern: RegExp; url: string }> = [
  { pattern: /unify-dev/i, url: 'https://dev.unifymentalhealth.com' },
  { pattern: /unify-staging/i, url: 'https://staging.unifymentalhealth.com' },
  { pattern: /unify-prod|production/i, url: 'https://secure.unifymentalhealth.com' },
];

const HOSTED_BROWSER_HOSTS = new Set([
  'dev.unifymentalhealth.com',
  'staging.unifymentalhealth.com',
  'secure.unifymentalhealth.com',
]);

export type PublicAppBaseUrlSecrets = Record<string, { valueString?: string } | undefined>;

/** Structural Medplum client — avoid importing `@medplum/core` (breaks some CJS bot paths). */
export interface PublicAppBaseUrlClient {
  getBaseUrl?: () => string;
  getActiveLogin?: () => { project?: { reference?: string } } | undefined | null;
  readResource?: (resourceType: 'Project', id: string) => PromiseLike<{ name?: string }>;
}

let secretsOverride: PublicAppBaseUrlSecrets | undefined;

export function configurePublicAppBaseUrlSecrets(secrets: PublicAppBaseUrlSecrets | undefined): void {
  secretsOverride = secrets;
}

export function resetPublicAppBaseUrlSecrets(): void {
  secretsOverride = undefined;
}

function normalizeAppBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function readSecretValue(secrets: PublicAppBaseUrlSecrets | undefined, keys: string[]): string | undefined {
  if (!secrets) return undefined;
  for (const key of keys) {
    const value = secrets[key]?.valueString?.trim();
    if (value) return value;
  }
  return undefined;
}

function readProcessEnv(keys: string[]): string | undefined {
  if (typeof process === 'undefined' || !process.env) return undefined;
  for (const key of keys) {
    const value = process.env[key]?.trim();
    if (value) return value;
  }
  return undefined;
}

/** Vite injects VITE_APP_URL in browser builds; absent in Medplum bot CJS bundles. */
function readViteAppUrl(): string | undefined {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
    const value = env?.VITE_APP_URL?.trim();
    return value || undefined;
  } catch {
    return undefined;
  }
}

/** Hosted Settings / test-send: use the origin the operator is actually on. */
function readKnownBrowserOrigin(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const origin = window.location?.origin?.trim();
    if (!origin) return undefined;
    const host = new URL(origin).hostname.toLowerCase();
    if (HOSTED_BROWSER_HOSTS.has(host)) return origin;
  } catch {
    return undefined;
  }
  return undefined;
}

function readConfiguredUrl(): string | undefined {
  const fromSecrets = readSecretValue(secretsOverride, ['APP_BASE_URL', 'VITE_APP_URL']);
  if (fromSecrets) return fromSecrets;

  // Explicit script override only — do not read VITE_APP_URL here. Vite inlines that
  // at build time, which would pin prod test-sends to whatever env the bundle used.
  const fromAppBase = readProcessEnv(['APP_BASE_URL']);
  if (fromAppBase) return fromAppBase;

  const fromBrowser = readKnownBrowserOrigin();
  if (fromBrowser) return fromBrowser;

  const fromViteProcess = readProcessEnv(['VITE_APP_URL']);
  if (fromViteProcess) return fromViteProcess;

  return readViteAppUrl();
}

/**
 * Public SPA origin for client-portal links in email templates and notification variables.
 *
 * Resolution order:
 *   1. APP_BASE_URL / VITE_APP_URL from configured bot secrets (rules-engine Lambda)
 *   2. process.env.APP_BASE_URL (scripts / CI)
 *   3. window.location.origin on hosted Unify hosts (dev / staging / secure)
 *   4. process.env.VITE_APP_URL / import.meta.env.VITE_APP_URL
 *   5. http://localhost:3015
 *
 * Intentionally does not import `@/config` — this module reads the Vite-only import.meta.env at
 * resolution time (guarded); `@/config` is load-safe in CJS bot bundles, but this file stays free of it.
 */
export function getPublicAppBaseUrl(): string {
  const configured = readConfiguredUrl();
  if (configured) return normalizeAppBaseUrl(configured);
  return LOCAL_DEFAULT;
}

/** Infer the public SPA origin from the Medplum FHIR host / project name. */
export async function inferHostedAppBaseUrl(
  medplum?: PublicAppBaseUrlClient,
): Promise<string | undefined> {
  const medplumBaseUrl = medplum?.getBaseUrl?.() ?? '';
  if (/localhost|127\.0\.0\.1/i.test(medplumBaseUrl)) {
    return LOCAL_DEFAULT;
  }

  if (/api\.medplum\.com/i.test(medplumBaseUrl) && medplum) {
    const projectRef = medplum.getActiveLogin?.()?.project?.reference;
    const readProject = medplum.readResource;
    if (projectRef?.startsWith('Project/') && readProject) {
      try {
        const project = await readProject('Project', projectRef.slice('Project/'.length));
        const name = project.name ?? '';
        for (const { pattern, url } of HOSTED_APP_URL_BY_PROJECT_NAME) {
          if (pattern.test(name)) return url;
        }
      } catch {
        // fall through to prod default
      }
    }
    return 'https://secure.unifymentalhealth.com';
  }

  return undefined;
}

/**
 * Same as {@link getPublicAppBaseUrl}, plus hosted-project inference when the
 * Medplum FHIR base is `api.medplum.com` and no APP_BASE_URL is configured.
 */
export async function resolvePublicAppBaseUrl(
  medplum?: PublicAppBaseUrlClient,
): Promise<string> {
  const configured = readConfiguredUrl();
  if (configured) return normalizeAppBaseUrl(configured);
  return (await inferHostedAppBaseUrl(medplum)) ?? LOCAL_DEFAULT;
}
